import { Lang } from "../context/LanguageContext";

export interface LegalSection {
  heading: string;
  body: string[];
}

export interface LegalDocument {
  slug: string;
  title: string;
  updatedAt: string;
  sections: LegalSection[];
}

// Company details: replace the bracketed placeholders with your actual trade
// registry / tax office information. These fields are required for the
// Distance Sales Agreement and Privacy Policy documents.
export const companyInfo = {
  legalName: "Haptone Technology Inc.",
  address:
    "Başıbüyük Mah. Süreyyapaşa Başıbüyük Yolu Sk. Teknopark No: 4/1 İç Kapı No: 1, Maltepe/İSTANBUL, Turkey",
  taxOffice: "Küçükyalı",
  taxNumber: "4571479907",
  mersisNo: "0457147990700001",
  email: "info@haptone.com",
  phone: "+90 543 171 29 23",
};

const legalDocumentsEn: LegalDocument[] = [
  {
    slug: "distance-sales-agreement",
    title: "Distance Sales Agreement",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. Parties",
        body: [
          `This Distance Sales Agreement ("Agreement") is entered into electronically between ${companyInfo.legalName} (${companyInfo.address}, Tax Office: ${companyInfo.taxOffice}, Tax No: ${companyInfo.taxNumber}, MERSIS No: ${companyInfo.mersisNo}) ("Seller") and the person placing an order through haptone.com and its subdomains ("Buyer").`,
        ],
      },
      {
        heading: "2. Subject of the Agreement",
        body: [
          "The subject of this Agreement is to determine the rights and obligations of the parties regarding the sale of the training program/service ordered electronically by the Buyer through the Seller's website, in accordance with applicable consumer protection legislation.",
        ],
      },
      {
        heading: "3. Key Features and Price of the Service",
        body: [
          "The name, content, date, duration, and total sale price (including all taxes) of the purchased training program are clearly stated on the order confirmation screen and in the order summary email sent to the Buyer.",
          "Payment is securely collected via credit card or debit card through the Seller's contracted payment service provider, Tami (a licensed payment institution).",
        ],
      },
      {
        heading: "4. Right of Withdrawal",
        body: [
          "The Buyer has the right to withdraw from the Agreement before the service is performed, without giving any reason and without paying a penalty.",
          "For training programs delivered on a specific date, if the withdrawal request is made before the delivery date, the amount paid is refunded. For cancellation requests made less than 7 days before the event start date, non-refundable amounts already paid to third parties (instructor, venue, accommodation, etc.) may be deducted before issuing a refund.",
          "Once the service has been fully performed with the Buyer's consent for a service scheduled on a specific date, the right of withdrawal can no longer be exercised after performance has been completed.",
        ],
      },
      {
        heading: "5. Delivery and Performance",
        body: [
          "The training program is delivered by the Seller on the announced date, time, and location (or online). Attendance details are sent to the Buyer's provided email address following order confirmation.",
        ],
      },
      {
        heading: "6. General Provisions",
        body: [
          "By confirming the order, the Buyer is deemed to have accepted all terms of this Agreement.",
          "Any disputes arising from this Agreement shall be resolved by the competent consumer arbitration committees or consumer courts, as applicable under local consumer protection law.",
        ],
      },
    ],
  },
  {
    slug: "privacy-policy",
    title: "Privacy Policy",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. Data Controller",
        body: [
          `${companyInfo.legalName} acts as the data controller for the personal data described below, processed in accordance with applicable data protection laws.`,
        ],
      },
      {
        heading: "2. Personal Data Collected",
        body: [
          "Full name, email address, phone number, billing/address details, and limited transaction data related to payment processing (card details are never stored on the Seller's servers; they are processed directly by the licensed payment service provider).",
        ],
      },
      {
        heading: "3. Purposes of Processing",
        body: [
          "Personal data is processed to manage order and payment processes, facilitate communication and participation in training programs, comply with legal obligations, and manage customer relations and inquiries.",
        ],
      },
      {
        heading: "4. Data Sharing",
        body: [
          "Your payment information is shared with the contracted payment service provider (Tami) solely to complete the transaction securely. Your personal data is not shared with third parties except where legally required.",
        ],
      },
      {
        heading: "5. Your Rights",
        body: [
          "You have the right to learn whether your personal data is processed, request information about the processing, learn the purpose of processing and whether it is used accordingly, know the third parties to whom your data is transferred, request correction of incomplete or inaccurate data, request deletion or destruction of your data, and object to processing.",
          `To exercise these rights, please contact us at ${companyInfo.email}.`,
        ],
      },
      {
        heading: "6. Cookies",
        body: [
          "Our website may use essential and analytical cookies to improve user experience and measure site performance. You can manage your cookie preferences through your browser settings.",
        ],
      },
    ],
  },
  {
    slug: "cancellation-and-refund-policy",
    title: "Cancellation and Refund Policy",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. Cancellation Requests",
        body: [
          `To cancel your registration for a training program, please contact us in writing at ${companyInfo.email} or by phone at ${companyInfo.phone} (Turkey-based registrations).`,
          "Cancellations made more than 30 days before the event date are fully refunded.",
          "Cancellations made 7-30 days before the event date will be refunded after deducting non-refundable third-party costs already incurred (instructor fees, venue, accommodation, materials, etc.).",
          "Cancellations made less than 7 days before the event date are not eligible for a refund; however, registration may be transferred to a future program upon request.",
        ],
      },
      {
        heading: "2. Seller-Initiated Cancellations",
        body: [
          "If the program is cancelled or postponed by the Seller, the Buyer may choose to receive a full refund of the amount paid or transfer the registration to a future date.",
        ],
      },
      {
        heading: "3. Refund Process",
        body: [
          "Approved refunds are credited back to the original credit/debit card used for payment, typically within 7-14 business days, depending on the issuing bank's processing time.",
        ],
      },
    ],
  },
  {
    slug: "delivery-and-fulfillment-terms",
    title: "Delivery and Fulfillment Terms",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. Nature of the Service",
        body: [
          "The purchased product is not a physical shipment but a training/program service delivered on a specified date and location (or online).",
        ],
      },
      {
        heading: "2. Notification and Attendance",
        body: [
          "Following payment confirmation, information regarding the program date, time, location, and attendance requirements is sent to the email address and/or phone number provided at checkout.",
          "For programs that include physical materials (certificate, training kit, etc.), these are handed to the participant during or at the end of the program; if shipping is required in exceptional cases, the Buyer will be notified separately.",
        ],
      },
      {
        heading: "3. Payment Confirmation",
        body: [
          "Registration is finalized once payment is confirmed through the payment gateway, and the Buyer receives order/invoice details by email.",
        ],
      },
    ],
  },
  {
    slug: "terms-of-use",
    title: "Terms of Use",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. General",
        body: [
          `By using this website, you agree to these Terms of Use for the services offered through the platform operated by ${companyInfo.legalName}.`,
        ],
      },
      {
        heading: "2. Intellectual Property",
        body: [
          "All content, logos, trademarks, and materials on this site are owned by the Seller and may not be reproduced, distributed, or used for commercial purposes without permission.",
        ],
      },
      {
        heading: "3. Limitation of Liability",
        body: [
          "In the event a program is postponed or cancelled due to force majeure (natural disasters, government decisions, pandemics, etc.), the Seller will offer alternative solutions to minimize any inconvenience.",
        ],
      },
      {
        heading: "4. Contact",
        body: [
          `For any questions, please contact us at ${companyInfo.email} or ${companyInfo.phone} (Turkey-based registrations).`,
        ],
      },
    ],
  },
];

const aboutDocumentEn: LegalDocument = {
  slug: "about-us",
  title: "About Us",
  updatedAt: "2026-08-28",
  sections: [
    {
      heading: "Who We Are",
      body: [
        `${companyInfo.legalName} is a technology company developing innovative simulation models and analysis systems for veterinary education and animal behaviour research.`,
        "Founded in 2021 by veterinarians, engineers, and researchers, Haptone aims to advance veterinary education, surgical skill development, and animal research by offering realistic and ethical alternatives to live animal use.",
      ],
    },
    {
      heading: "Haptone Shop",
      body: [
        "Haptone Shop, operated through haptone.com, is the platform where clinical and hands-on training programs are offered to universities, veterinarians, and researchers.",
        "The training programs listed on this site are delivered by expert instructors on scheduled dates and locations (or online).",
      ],
    },
    {
      heading: "Contact Information",
      body: [
        `Address: ${companyInfo.address}`,
        `Email: ${companyInfo.email}`,
        `Phone: ${companyInfo.phone}`,
      ],
    },
  ],
};

const legalDocumentsTr: LegalDocument[] = [
  {
    slug: "distance-sales-agreement",
    title: "Mesafeli Satış Sözleşmesi",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. Taraflar",
        body: [
          `İşbu Mesafeli Satış Sözleşmesi ("Sözleşme"), ${companyInfo.legalName} (${companyInfo.address}, Vergi Dairesi: ${companyInfo.taxOffice}, Vergi No: ${companyInfo.taxNumber}, MERSİS No: ${companyInfo.mersisNo}) ("Satıcı") ile haptone.com ve alt alan adları üzerinden sipariş veren kişi ("Alıcı") arasında elektronik ortamda akdedilmiştir.`,
        ],
      },
      {
        heading: "2. Sözleşmenin Konusu",
        body: [
          "İşbu Sözleşmenin konusu, Alıcı'nın Satıcı'ya ait internet sitesi üzerinden elektronik ortamda sipariş verdiği eğitim programı/hizmetinin satışına ilişkin olarak, yürürlükteki tüketicinin korunması mevzuatı hükümleri uyarınca tarafların hak ve yükümlülüklerinin belirlenmesidir.",
        ],
      },
      {
        heading: "3. Hizmetin Temel Özellikleri ve Fiyatı",
        body: [
          "Satın alınan eğitim programının adı, içeriği, tarihi, süresi ve tüm vergiler dâhil toplam satış bedeli, sipariş onay ekranında ve Alıcı'ya gönderilen sipariş özeti e-postasında açıkça belirtilmiştir.",
          "Ödeme; kredi kartı veya banka kartı ile Satıcı'nın anlaşmalı ödeme kuruluşu Tami (lisanslı bir ödeme kuruluşu) aracılığıyla güvenli şekilde tahsil edilir.",
        ],
      },
      {
        heading: "4. Cayma Hakkı",
        body: [
          "Alıcı, hizmet ifa edilmeden önce hiçbir gerekçe göstermeksizin ve cezai şart ödemeksizin Sözleşme'den cayma hakkına sahiptir.",
          "Belirli bir tarihte gerçekleştirilecek eğitim programları için, cayma talebi ifa tarihinden önce yapılırsa ödenen tutar iade edilir. Etkinlik başlangıç tarihinden 7 günden daha az bir süre kala yapılan iptal taleplerinde, üçüncü kişilere (eğitmen, mekân, konaklama vb.) zaten ödenmiş ve iadesi mümkün olmayan tutarlar iade edilecek bedelden düşülebilir.",
          "Belirli bir tarihte ifa edilecek hizmetin, Alıcı'nın onayıyla tam olarak ifa edilmesinden sonra cayma hakkı kullanılamaz.",
        ],
      },
      {
        heading: "5. Teslimat ve İfa",
        body: [
          "Eğitim programı, Satıcı tarafından duyurulan tarih, saat ve konumda (veya çevrim içi olarak) ifa edilir. Katılıma ilişkin bilgiler, sipariş onayının ardından Alıcı'nın belirttiği e-posta adresine gönderilir.",
        ],
      },
      {
        heading: "6. Genel Hükümler",
        body: [
          "Alıcı, siparişi onaylamakla işbu Sözleşme'nin tüm hükümlerini kabul etmiş sayılır.",
          "İşbu Sözleşme'den doğan uyuşmazlıklar, yürürlükteki tüketicinin korunması mevzuatı uyarınca yetkili tüketici hakem heyetleri veya tüketici mahkemeleri tarafından çözümlenir.",
        ],
      },
    ],
  },
  {
    slug: "privacy-policy",
    title: "Gizlilik Politikası",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. Veri Sorumlusu",
        body: [
          `${companyInfo.legalName}, yürürlükteki kişisel verilerin korunması mevzuatı uyarınca işlenen aşağıda açıklanan kişisel veriler bakımından veri sorumlusu sıfatıyla hareket etmektedir.`,
        ],
      },
      {
        heading: "2. Toplanan Kişisel Veriler",
        body: [
          "Ad soyad, e-posta adresi, telefon numarası, fatura/adres bilgileri ve ödeme işlemine ilişkin sınırlı işlem verileri (kart bilgileri Satıcı sunucularında hiçbir şekilde saklanmaz; doğrudan lisanslı ödeme kuruluşu tarafından işlenir).",
        ],
      },
      {
        heading: "3. İşleme Amaçları",
        body: [
          "Kişisel veriler; sipariş ve ödeme süreçlerinin yürütülmesi, eğitim programlarına iletişim ve katılım süreçlerinin sağlanması, yasal yükümlülüklerin yerine getirilmesi ve müşteri ilişkileri ile taleplerin yönetilmesi amacıyla işlenmektedir.",
        ],
      },
      {
        heading: "4. Verilerin Paylaşılması",
        body: [
          "Ödeme bilgileriniz, yalnızca işlemin güvenli şekilde tamamlanması amacıyla anlaşmalı ödeme kuruluşu (Tami) ile paylaşılır. Kişisel verileriniz, yasal zorunluluklar dışında üçüncü kişilerle paylaşılmaz.",
        ],
      },
      {
        heading: "5. Haklarınız",
        body: [
          "Kişisel verilerinizin işlenip işlenmediğini öğrenme, işlenmişse buna ilişkin bilgi talep etme, işlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme, verilerinizin aktarıldığı üçüncü kişileri bilme, eksik veya yanlış işlenmişse düzeltilmesini isteme, silinmesini veya yok edilmesini talep etme ve işlenmesine itiraz etme hakkına sahipsiniz.",
          `Bu haklarınızı kullanmak için ${companyInfo.email} adresinden bizimle iletişime geçebilirsiniz.`,
        ],
      },
      {
        heading: "6. Çerezler",
        body: [
          "İnternet sitemiz, kullanıcı deneyimini iyileştirmek ve site performansını ölçmek amacıyla zorunlu ve analitik çerezler kullanabilir. Çerez tercihlerinizi tarayıcı ayarlarınız üzerinden yönetebilirsiniz.",
        ],
      },
    ],
  },
  {
    slug: "cancellation-and-refund-policy",
    title: "İptal ve İade Politikası",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. İptal Talepleri",
        body: [
          `Bir eğitim programına ilişkin kaydınızı iptal etmek için ${companyInfo.email} adresinden yazılı olarak veya ${companyInfo.phone} numarasından (yurt içi kayıtlar için) bizimle iletişime geçebilirsiniz.`,
          "Etkinlik tarihinden 30 günden fazla önce yapılan iptallerde ödenen tutarın tamamı iade edilir.",
          "Etkinlik tarihinden 7-30 gün önce yapılan iptallerde, o ana kadar oluşmuş ve iadesi mümkün olmayan üçüncü taraf maliyetleri (eğitmen ücreti, mekân, konaklama, materyal vb.) düşüldükten sonra iade yapılır.",
          "Etkinlik tarihinden 7 günden daha az bir süre kala yapılan iptallerde iade hakkı bulunmamaktadır; ancak talep üzerine kayıt ileri bir tarihteki programa aktarılabilir.",
        ],
      },
      {
        heading: "2. Satıcı Kaynaklı İptaller",
        body: [
          "Programın Satıcı tarafından iptal edilmesi veya ertelenmesi durumunda Alıcı, ödenen tutarın tamamının iadesini veya kaydının ileri bir tarihe aktarılmasını talep edebilir.",
        ],
      },
      {
        heading: "3. İade Süreci",
        body: [
          "Onaylanan iadeler, ödemenin yapıldığı kredi/banka kartına, ilgili bankanın işlem süresine bağlı olarak genellikle 7-14 iş günü içinde yansıtılır.",
        ],
      },
    ],
  },
  {
    slug: "delivery-and-fulfillment-terms",
    title: "Teslimat ve İfa Koşulları",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. Hizmetin Niteliği",
        body: [
          "Satın alınan ürün fiziksel bir kargo değil, belirlenen bir tarihte ve konumda (veya çevrim içi olarak) ifa edilen bir eğitim/program hizmetidir.",
        ],
      },
      {
        heading: "2. Bilgilendirme ve Katılım",
        body: [
          "Ödeme onayının ardından programın tarihi, saati, konumu ve katılım koşullarına ilişkin bilgiler, ödeme sırasında belirtilen e-posta adresine ve/veya telefon numarasına gönderilir.",
          "Fiziksel materyal içeren programlarda (sertifika, eğitim kiti vb.) bu materyaller program sırasında veya sonunda katılımcıya teslim edilir; istisnai durumlarda kargo gerekmesi hâlinde Alıcı ayrıca bilgilendirilir.",
        ],
      },
      {
        heading: "3. Ödeme Onayı",
        body: [
          "Kayıt, ödeme ağ geçidi üzerinden ödemenin onaylanmasıyla kesinleşir ve Alıcı'ya e-posta yoluyla sipariş/fatura detayları iletilir.",
        ],
      },
    ],
  },
  {
    slug: "terms-of-use",
    title: "Kullanım Koşulları",
    updatedAt: "2026-08-28",
    sections: [
      {
        heading: "1. Genel",
        body: [
          `Bu internet sitesini kullanarak, ${companyInfo.legalName} tarafından işletilen platform üzerinden sunulan hizmetlere ilişkin işbu Kullanım Koşulları'nı kabul etmiş olursunuz.`,
        ],
      },
      {
        heading: "2. Fikri Mülkiyet",
        body: [
          "Bu sitedeki tüm içerik, logo, marka ve materyaller Satıcı'ya aittir ve izin alınmaksızın çoğaltılamaz, dağıtılamaz veya ticari amaçla kullanılamaz.",
        ],
      },
      {
        heading: "3. Sorumluluğun Sınırlandırılması",
        body: [
          "Mücbir sebepler (doğal afetler, resmi kararlar, salgın hastalıklar vb.) nedeniyle bir programın ertelenmesi veya iptal edilmesi hâlinde Satıcı, oluşabilecek mağduriyeti en aza indirmek için alternatif çözümler sunacaktır.",
        ],
      },
      {
        heading: "4. İletişim",
        body: [
          `Her türlü sorunuz için ${companyInfo.email} adresinden veya ${companyInfo.phone} (yurt içi kayıtlar için) numarasından bizimle iletişime geçebilirsiniz.`,
        ],
      },
    ],
  },
];

const aboutDocumentTr: LegalDocument = {
  slug: "about-us",
  title: "Hakkımızda",
  updatedAt: "2026-08-28",
  sections: [
    {
      heading: "Biz Kimiz",
      body: [
        `${companyInfo.legalName}, veteriner eğitimi ve hayvan davranışı araştırmaları için yenilikçi simülasyon modelleri ve analiz sistemleri geliştiren bir teknoloji şirketidir.`,
        "2021 yılında veteriner hekimler, mühendisler ve araştırmacılar tarafından kurulan Haptone; canlı hayvan kullanımına gerçekçi ve etik alternatifler sunarak veteriner eğitimini, cerrahi beceri gelişimini ve hayvan araştırmalarını ileriye taşımayı amaçlamaktadır.",
      ],
    },
    {
      heading: "Haptone Shop",
      body: [
        "Haptone Shop, haptone.com üzerinden işletilen; üniversitelere, veteriner hekimlere ve araştırmacılara klinik ve uygulamalı eğitim programlarının sunulduğu platformdur.",
        "Bu sitede yer alan eğitim programları, belirlenen tarih ve konumlarda (veya çevrim içi olarak) uzman eğitmenler tarafından verilmektedir.",
      ],
    },
    {
      heading: "İletişim Bilgileri",
      body: [
        `Adres: ${companyInfo.address}`,
        `E-posta: ${companyInfo.email}`,
        `Telefon: ${companyInfo.phone}`,
      ],
    },
  ],
};

export const legalDocumentsByLang: Record<Lang, LegalDocument[]> = {
  en: legalDocumentsEn,
  tr: legalDocumentsTr,
};

export const aboutDocumentByLang: Record<Lang, LegalDocument> = {
  en: aboutDocumentEn,
  tr: aboutDocumentTr,
};

export function getLegalDocuments(lang: Lang): LegalDocument[] {
  return legalDocumentsByLang[lang];
}

export function getAboutDocument(lang: Lang): LegalDocument {
  return aboutDocumentByLang[lang];
}

export function getLegalDocument(slug: string, lang: Lang): LegalDocument | undefined {
  const about = getAboutDocument(lang);
  if (slug === about.slug) return about;
  return getLegalDocuments(lang).find((doc) => doc.slug === slug);
}
